import { type FormEvent, useEffect, useMemo, useState } from "react";
import { ApiError, api, type Marca, type Modelo } from "@/lib/api";
import AvisoModelosSimilares from "@/components/AvisoModelosSimilares";
import Modal from "@/ui/Modal";
import { useToast } from "@/ui/Toast";

// Sentinel para la opcion "+ Crear..." dentro del <select>. Cualquier valor
// que NO pueda colisionar con un nombre real de marca/modelo.
const CREAR_SENTINEL = "__crear_nuevo__";

type Props = {
  marca: string;
  modelo: string;
  onChange: (marca: string, modelo: string) => void;
};

/**
 * Selector combinado de marca + modelo con creacion inline via Modal propio.
 * NUNCA usa window.prompt / window.confirm.
 *
 * - Marca: dropdown de las activas. Boton "+" abre modal para crear nueva.
 * - Modelo: dropdown filtrado por marca. Boton "+" abre modal para crear.
 * - Si el caso ya tiene marca/modelo libre que no esta en catalogo, lo
 *   respeta, SUGIERE los parecidos del catalogo ("Usar este") y solo
 *   despues ofrece agregarlo.
 * - Los descontinuados (activo=false) se cargan pero NO se ofrecen: solo
 *   aparecen si son el valor actual del caso, marcados como tales. Sin
 *   esto, desactivar un modelo hacia que sus casos vivos se vieran como
 *   "no esta en el catalogo".
 */
export default function MarcaModeloPicker({ marca, modelo, onChange }: Props) {
  const toast = useToast();
  const [marcas, setMarcas] = useState<Marca[]>([]);
  const [modelos, setModelos] = useState<Modelo[]>([]);
  const [loading, setLoading] = useState(true);
  const [crearMarcaOpen, setCrearMarcaOpen] = useState(false);
  const [crearModeloOpen, setCrearModeloOpen] = useState(false);
  const [crearModeloPrefill, setCrearModeloPrefill] = useState("");

  async function refresh() {
    setLoading(true);
    try {
      const [ms, mds] = await Promise.all([
        api.get<Marca[]>("/api/catalogos/marcas?incluir_inactivas=true"),
        api.get<Modelo[]>("/api/catalogos/modelos?incluir_inactivos=true"),
      ]);
      setMarcas(ms);
      setModelos(mds);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void refresh();
  }, []);

  const marcaActual = useMemo(
    () => marcas.find((m) => m.nombre.toLowerCase() === marca.toLowerCase()),
    [marcas, marca],
  );

  const modelosDeMarca = useMemo(() => {
    if (!marcaActual) return [];
    return modelos.filter((m) => m.marca_id === marcaActual.id);
  }, [modelos, marcaActual]);

  const modeloActual = useMemo(
    () =>
      modelosDeMarca.find(
        (m) => m.nombre.toLowerCase() === modelo.trim().toLowerCase(),
      ),
    [modelosDeMarca, modelo],
  );

  // Lo que el desplegable OFRECE: solo lo vigente. El descontinuado que ya
  // trae el caso se cuela para no perderlo al guardar, pero no se puede
  // elegir en un caso nuevo.
  const opcionesMarca = useMemo(() => {
    const activas = marcas.filter((m) => m.activo);
    if (marcaActual && !marcaActual.activo) {
      return [...activas, marcaActual].sort((a, b) => a.nombre.localeCompare(b.nombre));
    }
    return activas;
  }, [marcas, marcaActual]);

  const opcionesModelo = useMemo(() => {
    const activos = modelosDeMarca.filter((m) => m.activo);
    if (modeloActual && !modeloActual.activo) {
      return [...activos, modeloActual].sort((a, b) => a.nombre.localeCompare(b.nombre));
    }
    return activos;
  }, [modelosDeMarca, modeloActual]);

  const marcaEsLibre = marca.trim() !== "" && !marcaActual;
  const modeloEsLibre = modelo.trim() !== "" && !modeloActual;

  // Si llega un modelo SIN marca (p. ej. prefill de la IA) pero ese modelo existe en
  // el catálogo, resolver su marca automáticamente (CV200XEU -> Queclink).
  useEffect(() => {
    if (loading || marcaActual || !modelo.trim()) return;
    const match = modelos.filter(
      (x) => x.nombre.toLowerCase() === modelo.toLowerCase(),
    );
    if (match.length === 1) onChange(match[0].marca_nombre, match[0].nombre);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, modelos, modelo, marcaActual]);

  async function handleMarcaCreada(nombre: string) {
    try {
      const m = await api.post<Marca>("/api/catalogos/marcas", { nombre });
      toast.success("Marca creada", m.nombre);
      await refresh();
      onChange(m.nombre, "");
    } catch (err) {
      const msg = err instanceof ApiError ? err.detail : "Error";
      toast.error("No se pudo crear la marca", msg);
    }
  }

  async function handleModeloCreado(nombre: string) {
    if (!marcaActual) return;
    try {
      const m = await api.post<Modelo>("/api/catalogos/modelos", {
        marca_id: marcaActual.id,
        nombre,
      });
      toast.success("Modelo creado", `${m.marca_nombre} / ${m.nombre}`);
      await refresh();
      onChange(marcaActual.nombre, m.nombre);
    } catch (err) {
      const msg = err instanceof ApiError ? err.detail : "Error";
      toast.error("No se pudo crear el modelo", msg);
    }
  }

  async function agregarMarcaActualAlCatalogo() {
    if (!marca.trim()) return;
    try {
      const m = await api.post<Marca>("/api/catalogos/marcas", { nombre: marca.trim() });
      toast.success("Marca agregada al catálogo", m.nombre);
      await refresh();
    } catch (err) {
      const msg = err instanceof ApiError ? err.detail : "Error";
      toast.error("No se pudo agregar", msg);
    }
  }

  function agregarModeloActualAlCatalogo() {
    // Abre el modal de crear con el nombre precargado: ahí vive el aviso
    // de similares (antes era un POST directo sin chequeo).
    if (!modelo.trim() || !marcaActual) return;
    setCrearModeloPrefill(modelo.trim());
    setCrearModeloOpen(true);
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
      <div>
        <Label>Marca</Label>
        <div className="flex gap-1 mt-1">
          <select
            className="input flex-1"
            value={marcaActual?.nombre ?? ""}
            onChange={(e) => {
              if (e.target.value === CREAR_SENTINEL) {
                setCrearMarcaOpen(true);
                return; // no propagar al onChange del padre
              }
              onChange(e.target.value, "");
            }}
            disabled={loading}
          >
            <option value="">— elige una marca —</option>
            {opcionesMarca.map((m) => (
              <option key={m.id} value={m.nombre}>
                {m.nombre}
                {m.activo ? "" : " (descontinuada)"}
              </option>
            ))}
            {marcaEsLibre && <option value={marca}>{marca} (libre)</option>}
            <option value={CREAR_SENTINEL}>+ Crear marca nueva...</option>
          </select>
          {/* Puro ícono: se nombra con aria-label. Qué hace va en la Ayuda
              de «Nuevo caso». */}
          <button
            type="button"
            onClick={() => setCrearMarcaOpen(true)}
            aria-label="Crear marca nueva"
            className="px-2.5 text-base leading-none border border-accent-500 rounded text-accent-400 hover:text-white hover:bg-accent-500 transition"
          >
            +
          </button>
        </div>
        {marcaEsLibre && (
          <p className="text-xs text-amber-300 mt-1">
            "{marca}" no está en el catálogo.{" "}
            <button
              type="button"
              onClick={agregarMarcaActualAlCatalogo}
              className="underline hover:text-amber-200"
            >
              Agregarla
            </button>
          </p>
        )}
      </div>

      <div>
        <Label>Modelo</Label>
        <div className="flex gap-1 mt-1">
          <select
            className="input flex-1"
            value={modeloActual?.nombre ?? (modeloEsLibre ? modelo : "")}
            onChange={(e) => {
              if (e.target.value === CREAR_SENTINEL) {
                setCrearModeloPrefill("");
                setCrearModeloOpen(true);
                return;
              }
              onChange(marca, e.target.value);
            }}
            disabled={loading || (!marcaActual && !modeloEsLibre)}
          >
            <option value="">
              {marcaActual ? "— elige modelo —" : "— elige marca primero —"}
            </option>
            {opcionesModelo.map((m) => (
              <option key={m.id} value={m.nombre}>
                {m.nombre}
                {m.activo ? "" : " (descontinuado)"}
              </option>
            ))}
            {modeloEsLibre && <option value={modelo}>{modelo} (libre)</option>}
            {marcaActual && (
              <option value={CREAR_SENTINEL}>+ Crear modelo nuevo...</option>
            )}
          </select>
          {/* Gris sin marca: el desplegable de al lado ya lo dice a la vista
              («— elige marca primero —»). */}
          <button
            type="button"
            onClick={() => {
              setCrearModeloPrefill("");
              setCrearModeloOpen(true);
            }}
            disabled={!marcaActual}
            aria-label="Crear modelo nuevo"
            className="px-2.5 text-base leading-none border border-accent-500 rounded text-accent-400 hover:text-white hover:bg-accent-500 transition disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-accent-400"
          >
            +
          </button>
        </div>
        {modeloEsLibre && !marcaActual && (
          <p className="text-xs text-amber-300 mt-1">
            Modelo &ldquo;{modelo}&rdquo; detectado — elige su marca arriba para
            completarlo.
          </p>
        )}
      </div>

      {/* Ancho completo (no dentro de la columna del modelo): el aviso lista
          nombres de catálogo y en media columna se parte en 5 renglones.
          El orden importa — primero los parecidos que YA existen y luego
          "Agregarlo", para que el remedio bueno se vea antes que el que
          crea un clon. */}
      {modeloEsLibre && marcaActual && (
        <div className="md:col-span-2">
          <AvisoModelosSimilares
            nombre={modelo}
            className="!mt-0"
            onUsar={(m) => onChange(m.marca_nombre, m.nombre)}
          />
          <p className="text-xs text-amber-300 mt-1">
            "{modelo}" no está en el catálogo.{" "}
            <button
              type="button"
              onClick={agregarModeloActualAlCatalogo}
              className="underline hover:text-amber-200"
            >
              Agregarlo
            </button>
          </p>
        </div>
      )}

      <CrearModal
        open={crearMarcaOpen}
        title="Nueva marca"
        label="Nombre de la marca"
        placeholder="ej. Ruptela, Queclink, JIMI"
        onClose={() => setCrearMarcaOpen(false)}
        onSubmit={handleMarcaCreada}
      />

      {marcaActual && (
        <CrearModal
          open={crearModeloOpen}
          title={`Nuevo modelo de ${marcaActual.nombre}`}
          label="Nombre del modelo"
          placeholder="ej. Trace5 LTE LA, GV58LAU"
          initialNombre={crearModeloPrefill}
          onClose={() => setCrearModeloOpen(false)}
          onSubmit={handleModeloCreado}
          conAvisoSimilares
          onUsarExistente={(m) => {
            onChange(m.marca_nombre, m.nombre);
            setCrearModeloOpen(false);
          }}
        />
      )}
    </div>
  );
}

function Label({ children }: { children: React.ReactNode }) {
  return <span className="text-xs uppercase tracking-wide text-zinc-400">{children}</span>;
}

// ============================================================================
// Modal generico para crear marca o modelo (reutilizable)
// ============================================================================

type CrearModalProps = {
  open: boolean;
  title: string;
  label: string;
  placeholder: string;
  /** Nombre precargado (p. ej. el modelo libre que se va a catalogar). */
  initialNombre?: string;
  onClose: () => void;
  onSubmit: (nombre: string) => Promise<void> | void;
  /** Muestra el aviso de modelos similares mientras se escribe. */
  conAvisoSimilares?: boolean;
  /** "Usar este" en el aviso: elige el modelo existente en vez de crear. */
  onUsarExistente?: (m: Modelo) => void;
};

function CrearModal({
  open,
  title,
  label,
  placeholder,
  initialNombre,
  onClose,
  onSubmit,
  conAvisoSimilares,
  onUsarExistente,
}: CrearModalProps) {
  const [nombre, setNombre] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [haySimilares, setHaySimilares] = useState(false);

  useEffect(() => {
    if (!open) {
      setNombre("");
      setSubmitting(false);
      setHaySimilares(false);
    } else {
      setNombre(initialNombre ?? "");
      // Reset explícito: una respuesta tardía del fetch anterior pudo
      // dejarlo pegado en true con el modal cerrado.
      setHaySimilares(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  async function handle(e: FormEvent) {
    e.preventDefault();
    const v = nombre.trim();
    if (!v) return;
    setSubmitting(true);
    try {
      await onSubmit(v);
      onClose();
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal
      open={open}
      onOpenChange={(v) => !v && onClose()}
      title={title}
      size="sm"
      footer={
        <>
          <button type="button" onClick={onClose} className="btn-ghost">Cancelar</button>
          <button
            type="button"
            onClick={handle}
            disabled={!nombre.trim() || submitting}
            className="btn-primary disabled:opacity-50"
          >
            {submitting
              ? "Creando..."
              : conAvisoSimilares && haySimilares
                ? "Crear de todos modos"
                : "Crear"}
          </button>
        </>
      }
    >
      <form onSubmit={handle}>
        <Label>{label}</Label>
        <input
          type="text"
          autoFocus
          className="input mt-1"
          value={nombre}
          onChange={(e) => setNombre(e.target.value)}
          placeholder={placeholder}
        />
        {conAvisoSimilares && (
          <AvisoModelosSimilares
            nombre={nombre}
            onHaySimilares={setHaySimilares}
            onUsar={
              onUsarExistente &&
              ((m) => {
                onUsarExistente(m);
                onClose();
              })
            }
          />
        )}
      </form>
    </Modal>
  );
}
